import { Component } from '@theme/component';
import { SlideshowSelectEvent } from '@theme/events';
import { prefersReducedMotion } from '@theme/utilities';
import { scrollIntoView } from '@theme/scrolling';
import { DialogCloseEvent } from '@theme/dialog';

/**
 * @typedef {object} GalleryRefs
 * @property {HTMLElement} viewport
 * @property {HTMLElement} track
 * @property {HTMLElement[]} slides
 * @property {HTMLButtonElement[]} [thumbnails]
 * @property {HTMLElement} [thumbnailsContainer]
 * @property {HTMLElement} [arrows]
 * @property {HTMLElement} [counter]
 * @property {HTMLElement} [current]
 * @property {HTMLElement} [status]
 * @property {HTMLButtonElement} [previous]
 * @property {HTMLButtonElement} [next]
 * @extends Component<GalleryRefs>
 */
class LussommoGallerySlider extends Component {
  requiredRefs = ['viewport', 'track', 'slides'];
  #controller = new AbortController();
  #current = 0;
  #initialMediaId;
  /** @type {{ x: number, y: number } | undefined} */
  #touchStart;
  #ignoreClickUntil = 0;
  /** @type {number | null} */
  #zoomIndex = null;

  connectedCallback() {
    super.connectedCallback();
    this.#bindListeners();
    this.dataset.enhanced = 'true';
    this.#initialMediaId = this.dataset.initialMediaId;
    this.select(this.initialIndex, undefined, { animate: false });
  }

  updatedCallback() {
    super.updatedCallback();
    this.#bindListeners();
    const selectedMediaChanged = this.#initialMediaId !== this.dataset.initialMediaId;
    this.#initialMediaId = this.dataset.initialMediaId;
    this.select(selectedMediaChanged ? this.initialIndex : Math.min(this.#current, this.slides.length - 1), undefined, { animate: false });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
    this.#touchStart = undefined;
    this.#zoomIndex = null;
  }

  #bindListeners() {
    this.#controller.abort();
    this.#controller = new AbortController();
    this.#touchStart = undefined;
    const { signal } = this.#controller;
    this.refs.viewport.addEventListener('pointerdown', this.#startSwipe, { signal });
    this.refs.viewport.addEventListener('pointerup', this.#finishSwipe, { signal });
    this.refs.viewport.addEventListener('pointercancel', this.#cancelSwipe, { signal });
    window.addEventListener(DialogCloseEvent.eventName, this.#restoreZoomFocus, { signal });
  }

  /** Native media-gallery and zoom-dialog consume this public slide list. */
  get slides() { return this.refs.slides ?? []; }
  get current() { return this.#current; }
  get initialIndex() { return Math.max(0, this.slides.findIndex(slide => slide.dataset.mediaId === this.dataset.initialMediaId)); }

  /**
   * @param {number|string} input
   * @param {Event} [event]
   * @param {{ animate?: boolean }} [options]
   */
  select(input, event, { animate = true } = {}) {
    const requested = Number(input);
    if (!Number.isFinite(requested) || !this.slides.length) return;
    event?.preventDefault();
    const index = Math.max(0, Math.min(Math.trunc(requested), this.slides.length - 1));
    const previousIndex = this.#current;
    this.#current = index;
    this.refs.track.style.transitionDuration = !animate || prefersReducedMotion() ? '0ms' : '300ms';
    const direction = getComputedStyle(this).direction === 'rtl' ? 1 : -1;
    this.refs.track.style.transform = `translate3d(${direction * index * 100}%, 0, 0)`;
    for (const [position, slide] of this.slides.entries()) {
      slide.setAttribute('aria-hidden', String(position !== index));
      slide.inert = position !== index;
      if (position !== index) {
        for (const media of slide.querySelectorAll('deferred-media, product-model')) {
          if (typeof media.pauseMedia === 'function') media.pauseMedia();
        }
      }
    }
    for (const [position, thumbnail] of (this.refs.thumbnails ?? []).entries()) {
      thumbnail.setAttribute('aria-pressed', String(position === index));
    }
    if (this.refs.current) this.refs.current.textContent = String(index + 1);
    if (this.refs.status) this.refs.status.textContent = (this.dataset.slideStatus ?? '__INDEX__ / __LENGTH__').replace('__INDEX__', String(index + 1)).replace('__LENGTH__', String(this.slides.length));
    if (this.refs.previous) this.refs.previous.disabled = index === 0;
    if (this.refs.next) this.refs.next.disabled = index === this.slides.length - 1;
    for (const control of [this.refs.arrows, this.refs.counter, this.refs.thumbnailsContainer]) {
      if (control) control.hidden = this.slides.length < 2;
    }
    const thumbnail = this.refs.thumbnails?.[index];
    if (thumbnail && this.refs.thumbnailsContainer) {
      scrollIntoView(thumbnail, { ancestor: this.refs.thumbnailsContainer, block: 'center', inline: 'center', behavior: !event || prefersReducedMotion() ? 'instant' : 'smooth' });
    }
    if (index !== previousIndex) this.dispatchEvent(new SlideshowSelectEvent({ index, previousIndex, userInitiated: event != null, trigger: 'select', slide: this.slides[index], id: this.slides[index].id }));
  }

  /** @param {Event} [event] */
  previous(event) { this.select(this.#current - 1, event); }
  /** @param {Event} [event] */
  next(event) { this.select(this.#current + 1, event); }

  /** @param {KeyboardEvent} event */
  handleKeyDown(event) {
    const rtl = getComputedStyle(this).direction === 'rtl';
    const focused = document.activeElement;
    // Media controls own seeking and model rotation keys, even through the event proxy.
    if (focused instanceof HTMLElement && focused.closest('deferred-media, product-model, video, audio, iframe, model-viewer')) return;
    const zoomHadFocus = focused instanceof HTMLElement && this.contains(focused) && focused.classList.contains('lussommo-gallery__zoom');
    if (event.key === 'ArrowRight') this.select(this.#current + (rtl ? -1 : 1), event);
    else if (event.key === 'ArrowLeft') this.select(this.#current + (rtl ? 1 : -1), event);
    else if (event.key === 'Home') this.select(0, event);
    else if (event.key === 'End') this.select(this.slides.length - 1, event);
    else return;
    if (zoomHadFocus) {
      this.slides[this.#current]?.querySelector('button')?.focus({ preventScroll: true });
    }
  }

  /** @param {number} index @param {PointerEvent} event */
  zoom(index, event) {
    if (performance.now() < this.#ignoreClickUntil) { event.preventDefault(); return; }
    const gallery = /** @type {import('./media-gallery').MediaGallery | null} */ (this.closest('media-gallery'));
    if (!gallery) return;
    this.#zoomIndex = index;
    gallery.zoom(index, event);
  }

  #restoreZoomFocus = () => {
    if (this.#zoomIndex == null) return;
    const gallery = this.closest('media-gallery');
    if (gallery?.querySelector('dialog')?.open) return;
    const changedSlide = this.#zoomIndex !== this.#current;
    this.#zoomIndex = null;
    if (changedSlide) this.slides[this.#current]?.querySelector('button')?.focus({ preventScroll: true });
  };

  /** @param {PointerEvent} event */
  #startSwipe = (event) => {
    if (event.pointerType !== 'touch') return;
    if (event.target instanceof HTMLElement && event.target.closest('deferred-media, product-model')) return;
    this.#touchStart = { x: event.clientX, y: event.clientY };
  };

  /** @param {PointerEvent} event */
  #finishSwipe = (event) => {
    if (!this.#touchStart || event.pointerType !== 'touch') return;
    const dx = event.clientX - this.#touchStart.x;
    const dy = event.clientY - this.#touchStart.y;
    this.#touchStart = undefined;
    if (Math.abs(dx) < 35 || Math.abs(dx) < Math.abs(dy) * 1.4) return;
    this.#ignoreClickUntil = performance.now() + 400;
    const rtl = getComputedStyle(this).direction === 'rtl';
    this.select(this.#current + (dx < 0 !== rtl ? 1 : -1), event);
  };

  #cancelSwipe = () => { this.#touchStart = undefined; };
}

if (!customElements.get('lussommo-gallery-slider')) customElements.define('lussommo-gallery-slider', LussommoGallerySlider);
